// Device binding (docs/platform/ClientShell.md "Input"): keyboard and mouse to the post-binder
// channel values the sim records (ARCHITECTURE "InputFrame = the post-binder values of the
// recordable channels"; device binding runs in the client). Port of the default keyboard/mouse
// bindings of `UserMan::InitBinder` 0xcca460 (pad layout UPL_ONE, the default) and
// Scripts/DefaultKeybindings.txt, with the mapper semantics of docs/re/atlas/reviews/input.md:
// alternative bindings of one channel merge with `InputMapXor` (two active alternatives cancel to
// 0); AnyInput/AnyRecordableInput as `UserMan::Pump(Peep*)` 0xcc8820 computes them in the local
// branch (§2.7). Camera channels are not recordable: the client's camera reads them per sim step
// (`cameraInput`, render/camera.ts `CameraMan.readInput`). The mouse is the original's trapped
// cursor (Pointer Lock, docs/platform/RenderFidelity.md "Manual camera control"): its motion
// pans the camera and moves the target cursor (`takeMotion`, TargetWorldX/Y as the worker writes
// them per tick); an uncaptured, visible cursor moves nothing.
import { CH, CHANNELS, NUM_CHANNELS } from './channels.ts';
import { CAMERA_CHANNELS } from '../render/camera.ts';

const f = Math.fround;
/** `InputService::m_fWheelScale` (0.01): a Qt wheel notch (`QWheelEvent` qt4D 120) adds 1.2 to the
 * `MouseWheel` channel; DOM deltas convert at 100 px or 3 lines a notch. */
const WHEEL_SCALE = f(0.01);

/** Channel -> its alternative device inputs (KeyboardEvent.code, or Mouse0/Mouse2 buttons). */
const BINDINGS: Record<string, string[]> = {
  Select: ['Enter', 'Mouse0'],
  Cancel: ['Escape'],
  MarqueeSizeToggle: ['KeyR', 'Mouse2'],
  FastForward: ['ShiftLeft'],
  SheepLock: ['KeyT'],
  SelectRight: ['Mouse2'],
  CameraUp: ['Numpad8'], CameraDown: ['Numpad2'], CameraLeft: ['Numpad4'], CameraRight: ['Numpad6'],
  CameraZoomToggle: ['ControlLeft'],
  CameraCentre: ['Mouse1'],
  WormLeft: ['ArrowLeft'], WormRight: ['ArrowRight'], WormUp: ['ArrowUp'], WormDown: ['ArrowDown'],
  WormJumpSecondary: ['Backspace'],
  WormJumpPrimary: ['Enter'],
  WormFireWeapon: ['Space'],
  WormAltFireWeapon: ['Enter'],
  WormEnterExitVehicle: ['KeyE'],
  WormRequestReplay: ['KeyR'],
  Weapon1: ['F1'], Weapon2: ['F2'], Weapon3: ['F3'], Weapon4: ['F4'], Weapon5: ['F5'], Weapon6: ['F6'], Weapon7: ['F7'], Weapon8: ['F8'], Weapon9: ['F9'],
  FuseNext: ['Tab'], FusePrev: ['KeyQ'],
  FuseTime1: ['Digit1'], FuseTime2: ['Digit2'], FuseTime3: ['Digit3'], FuseTime4: ['Digit4'], FuseTime5: ['Digit5'], FuseTime6: ['Digit6'],
  WormPanelNext: ['KeyN'],
  DropCrate: ['Space'],
  MouseDrag: ['Mouse0'],
  // `InitBinder`: the keyboard's "," and "." (GirderRotateRight's other alternative is the pad's
  // RButton bumper); the right mouse button cancels the targeting (TargetCancel) and toggles the
  // weapons panel, it never rotates a girder.
  GirderRotateLeft: ['Comma'], GirderRotateRight: ['Period'],
  TargetSelect: ['Mouse0', 'Space'],
  TargetDirection: ['Tab'],
  TargetCancel: ['Enter', 'Mouse2'],
  TargetLeft: ['KeyA', 'ArrowLeft'], TargetRight: ['KeyD', 'ArrowRight'], TargetUp: ['KeyW', 'ArrowUp'], TargetDown: ['KeyS', 'ArrowDown'],
  NavigateLeft: ['ArrowLeft', 'KeyA'], NavigateRight: ['ArrowRight', 'KeyD'], NavigateUp: ['ArrowUp', 'KeyW'], NavigateDown: ['ArrowDown', 'KeyS'],
};

/** `InputMapXor`: A == 0 -> B; B == 0 -> A; else 0. */
function xor(a: number, b: number): number {
  if (a === 0) return b;
  if (b === 0) return a;
  return 0;
}

/** The keyboard parts of the UserMan channels the client does not bind (UPL_ONE): UserMenu from
 * DefaultKeybindings.txt, the others from `UserMan::InitBinder` 0xcca460 (NavigateTabLeft and
 * NavigateTabRight: its second SetMapper, which replaces the first). [] = a mapper without a
 * keyboard `Get` (pad or mouse only). */
const UI_KEYS: Record<string, string[]> = {
  UserMenu: ['Escape'], NewUI_WeaponPanel: ['ShiftRight'], NewUI_Pause: ['Escape'], NewUI_Context1: ['F5'],
  NavigateTabLeft: ['KeyZ'], NavigateTabRight: ['KeyX'],
  CameraAxisX: [], CameraAxisY: [], CameraZoomInPad: [], CameraZoomOutPad: [], TargetLeftRight: [], TargetUpDown: [],
};

/** `InputLegacy::GetKeyboardDevice` 0xcc2ea0's action names (the names a binder definition's
 * keyboard `Get` carries: `InputMapGet::GetDefinition` 0xdadf50 writes the device's name of the
 * key) by KeyboardEvent.code. */
function keyActionName(code: string): string | null {
  let m = /^Key([A-Z])$/.exec(code) ?? /^Digit([0-9])$/.exec(code);
  if (m) return m[1].toLowerCase();
  if ((m = /^Numpad([0-9])$/.exec(code))) return `NumPad${m[1]}`;
  if (/^F[0-9]{1,2}$/.test(code)) return code;
  const named: Record<string, string> = {
    Enter: 'Return', Escape: 'Escape', Space: 'Space', Tab: 'Tab', Backspace: 'Backspace', ShiftLeft: 'Lshift', ShiftRight: 'Rshift',
    ControlLeft: 'Lctrl', ControlRight: 'Rctrl', ArrowLeft: 'Left', ArrowRight: 'Right', ArrowUp: 'Up', ArrowDown: 'Down',
    NumpadMultiply: 'NumPadMultiply', Home: 'Home', End: 'End', PageUp: 'PageUp', PageDown: 'PageDown', Insert: 'Insert', Delete: 'Delete',
    Comma: ',', Period: '.',
  };
  return named[code] ?? null;
}

/** The keyboard `Get`s of channel `name`'s binder definition (`InputBinder::GetDefinition`
 * 0xc40500 of the client's bindings) as keyboard action names, in Xor order; null when the
 * definition has no such channel. PCControlTextHelper reads it (ui/ip2/controls.ts). */
export function keyboardActions(name: string): string[] | null {
  const codes = BINDINGS[name] ?? UI_KEYS[name];
  if (!codes) return null;
  return codes.map(keyActionName).filter((a): a is string => a !== null);
}

export class Binder {
  /** The post-binder value of every channel. */
  values = new Float32Array(NUM_CHANNELS);
  private down = new Set<string>();
  /** Keys and buttons pressed since the last `update`: a press released before it (a touchpad
   * tap) is still down for one update. */
  private pressed = new Set<string>();
  private pulses = new Map<string, number>();
  private pointer: { x: number; y: number } | null = null;
  enabled = true;
  /** In play without a local menu (set by the match screen): the original hides and traps the
   * cursor (`QtShell_App::SetMouseCursorVisibility` 0xa79330, `QtSystem_Window::TrapCursor`), so
   * a press on the canvas captures the pointer (Pointer Lock). */
  captureWanted = false;
  /** The browser refused the last capture (`pointerlockerror` or a rejected request). */
  captureRefused = false;
  /** Tests: headless WebKit refuses Pointer Lock, so `lock` grants an emulated capture whose
   * motion comes from synthetic `mousemove` events' `movementX/Y`. */
  emulateLock = false;
  private emulatedLock = false;
  /** `RawInputTranslator::m_fMouseDeltaX/Y` (x right, y down) and `m_fMouseWheelDelta` since the
   * last camera sample. */
  private delta = { x: 0, y: 0 };
  /** The same motion since the last `takeMotion`: the target cursor's TargetWorldX/Y. */
  private motion = { x: 0, y: 0 };
  private wheel = 0;
  /** The canvas the pointer is locked to (`QtSystem_Window::TrapCursor`: in a match the original
   * hides and traps the cursor and sends its relative motion). */
  private target: HTMLElement | null = null;
  /** CSS pixels per `movementX/Y` unit. The original's deltas are Qt's device-independent pixels
   * (`ProcessMouseMove` 0xa76540 scales only the absolute position by the pixel ratio), the
   * canvas draws CSS pixels; browsers report `movementX/Y` in physical (Chromium), CSS (Firefox)
   * or device-independent (Safari) pixels (w3c/pointerlock#42). Measured on the visible
   * cursor's moves (client position against movement); until then Chromium's physical pixels
   * are assumed there. */
  private movementScale = /Chrome\//.test(navigator.userAgent) ? 1 / devicePixelRatio : 1;
  private calibration = { client: 0, movement: 0 };
  /** The visible cursor's last client position (null after a capture or leaving the canvas). */
  private lastClient: { x: number; y: number } | null = null;

  attach(target: HTMLElement): void {
    this.target = target;
    window.addEventListener('keydown', e => {
      if (!this.enabled || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code.startsWith('F') && e.code.length <= 3) e.preventDefault();
      if (['Space', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Backspace'].includes(e.code)) e.preventDefault();
      this.down.add(e.code);
      this.pressed.add(e.code);
    });
    window.addEventListener('keyup', e => { this.down.delete(e.code); });
    window.addEventListener('blur', () => this.down.clear());
    target.addEventListener('mousedown', e => {
      if (!this.enabled) return;
      // Uncaptured in play, a press captures the pointer and does nothing else (the original's
      // cursor is always trapped in play, so it has no capturing press): the left button would
      // select, fire or place a target. The middle button still centres (CameraCentre); the
      // right one does not capture (it opens the weapons panel, which shows the cursor).
      if (this.captureWanted && !this.locked() && e.button !== 2) {
        this.lock();
        if (e.button === 0) return;
      }
      this.down.add(`Mouse${e.button}`);
      this.pressed.add(`Mouse${e.button}`);
    });
    window.addEventListener('mouseup', e => this.down.delete(`Mouse${e.button}`));
    target.addEventListener('contextmenu', e => e.preventDefault());
    document.addEventListener('pointerlockchange', () => {
      this.lastClient = null;
      if (this.locked()) this.captureRefused = false;
    });
    document.addEventListener('pointerlockerror', () => { this.captureRefused = true; });
    target.addEventListener('mousemove', e => {
      const r = target.getBoundingClientRect();
      if (this.locked()) {
        // The trapped cursor's relative motion (`QtSystem_Window::ProcessMouseMove` 0xa76540 →
        // `RawInputTranslator::AddPointerMove` 0xd2f320); the game's cursor (`m_GuiPos`) follows
        // it inside the screen (`RawInputTranslator::UpdatePointer` 0xd2f4e0).
        const dx = f(e.movementX * this.movementScale), dy = f(e.movementY * this.movementScale);
        const c = this.pointer ?? { x: r.width / 2, y: r.height / 2 };
        this.pointer = { x: Math.max(0, Math.min(r.width, c.x + dx)), y: Math.max(0, Math.min(r.height, c.y + dy)) };
        this.delta.x = f(this.delta.x + dx);
        this.delta.y = f(this.delta.y + dy);
        this.motion.x = f(this.motion.x + dx);
        this.motion.y = f(this.motion.y + dy);
        return;
      }
      // A visible cursor moves nothing: the original shows it only while a local menu is open,
      // which stops the camera's mouse input (`CameraMan::ParseInput` 0xb651e0). Its moves
      // measure the browser's `movementX/Y` unit.
      const p = { x: e.clientX - r.left, y: e.clientY - r.top };
      const last = this.lastClient;
      const m = Math.abs(e.movementX) + Math.abs(e.movementY);
      if (last && m > 0) {
        const cal = this.calibration;
        cal.client += Math.abs(p.x - last.x) + Math.abs(p.y - last.y);
        cal.movement += m;
        if (cal.movement >= 64) {
          this.movementScale = Math.max(0.2, Math.min(5, cal.client / cal.movement));
          cal.client = 0;
          cal.movement = 0;
        }
      }
      this.lastClient = p;
      this.pointer = p;
    });
    target.addEventListener('mouseleave', () => { this.lastClient = null; });
    target.addEventListener('wheel', e => {
      e.preventDefault();
      const notch = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? 120 : 1.2;
      this.wheel = f(this.wheel + f(f(Math.round(-e.deltaY * notch)) * WHEEL_SCALE));
    }, { passive: false });
  }

  /** Whether the pointer is captured (locked to the canvas). */
  locked(): boolean {
    return this.emulatedLock || (!!this.target && document.pointerLockElement === this.target);
  }

  /** Captures the pointer (from a press or a pick: browsers lock only during a user gesture). */
  lock(): void {
    if (!this.target || this.locked()) return;
    this.lastClient = null;
    if (this.emulateLock) {
      this.emulatedLock = true;
      return;
    }
    const p = this.target.requestPointerLock() as unknown as Promise<void> | undefined;
    p?.catch?.(() => { this.captureRefused = true; });
  }

  unlock(): void {
    this.emulatedLock = false;
    this.lastClient = null;
    if (this.target && document.pointerLockElement === this.target) document.exitPointerLock();
  }

  /** One sim step's values of the camera channels (`CAMERA_CHANNELS` order): the binder's keys
   * and buttons, the game cursor normalised (y up), the pointer delta and the wheel since the
   * last sample (`RawInputTranslator::CursorUpdate`, `LogicUpdate`): `CameraZoomIn` = the wheel
   * mapped 0..90 → 0..90, `CameraZoomOut` = -90..0 → 90..0 (`UserMan::InitBinder` 0xcca460).
   * The pad channels stay 0 (no pad). */
  cameraInput(out: Float32Array): void {
    out.fill(0);
    CAMERA_CHANNELS.forEach((name, i) => { out[i] = this.values[CH[name]] ?? 0; });
    const r = this.target?.getBoundingClientRect();
    out[10] = this.pointer && r?.width ? this.pointer.x / r.width : 0.5;
    out[11] = this.pointer && r?.height ? 1 - this.pointer.y / r.height : 0.5;
    if (this.enabled) {
      out[12] = this.delta.x;
      out[13] = this.delta.y;
      const w = this.wheel;
      out[15] = w < 0 ? 0 : w > 90 ? 90 : f(f(f(w - 0) * f(90 - 0)) / f(90 - 0));
      out[16] = w < -90 ? 90 : w > 0 ? 0 : f(f(f(f(w + 90) * f(0 - 90)) / f(0 + 90)) + 90);
    }
    this.delta = { x: 0, y: 0 };
    this.wheel = 0;
  }

  /** The captured pointer's motion since the last call (screen pixels, x right, y down), null
   * when it did not move: `RawInputTranslator::m_fMouseDeltaX/Y`, which `UpdatePointer` 0xd2f4e0
   * publishes as TargetWorldX/Y (not a world position: its `NormalisedToWorld` result is thrown
   * away) and `TargetHelper::ProcessTargetInput` 0xc7a2b0 adds to the target (x, -y). */
  takeMotion(): [number, number] | null {
    const m = this.motion;
    if (m.x === 0 && m.y === 0) return null;
    this.motion = { x: 0, y: 0 };
    return [m.x, m.y];
  }

  /** Presses `code` for `frames` animation frames (weapon panel picks; tests). */
  pulse(code: string, frames = 2): void {
    this.pulses.set(code, frames);
  }

  isDown(code: string): boolean {
    return this.down.has(code) || this.pressed.has(code) || (this.pulses.get(code) ?? 0) > 0;
  }

  /** Recomputes every channel; returns whether a value changed. TargetWorldX/Y and MouseMoved
   * stay 0: the worker writes them per driven tick from the posted motion. */
  update(): boolean {
    const next = new Float32Array(NUM_CHANNELS);
    if (this.enabled) {
      for (const [name, codes] of Object.entries(BINDINGS)) {
        const ch = CH[name];
        if (ch === undefined) continue;
        let v = 0;
        for (const c of codes) v = xor(v, this.isDown(c) ? 1 : 0);
        next[ch] = v;
      }
      // AnyInput: the first non-zero raw key value, else the first non-zero any-input channel;
      // AnyRecordableInput: the first non-zero channel that is any-input and recordable.
      let any = this.down.size > 0 || this.pressed.size > 0 ? 1 : 0;
      let anyRec = 0;
      for (let ch = 0; ch < NUM_CHANNELS && !(any && anyRec); ch++) {
        const [, rec, anyIn] = CHANNELS[ch];
        if (!anyIn || next[ch] === 0) continue;
        if (!any) any = next[ch];
        if (rec && !anyRec) anyRec = next[ch];
      }
      next[CH.AnyInput] = any;
      next[CH.AnyRecordableInput] = anyRec;
    }
    this.pressed.clear();
    for (const [k, n] of this.pulses) {
      if (n <= 1) this.pulses.delete(k);
      else this.pulses.set(k, n - 1);
    }
    let changed = false;
    for (let i = 0; i < NUM_CHANNELS; i++) {
      if (!Object.is(next[i], this.values[i])) { changed = true; break; }
    }
    this.values = next;
    return changed;
  }
}
